import {Component, EventEmitter, forwardRef, Inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ReplaySubject} from 'rxjs';
import {PartnerService} from '../../services/partner.service';
import {Partner} from '../../interface/partner';
import {debounceTime, distinctUntilChanged, startWith, takeUntil} from 'rxjs/operators';
import {ListResponse} from '../../interface/response/list-response';
import {DOCUMENT} from '@angular/common';
import {MatSelectChange} from '@angular/material/select';
import {PARTNER_STATUS_ACTIVE, PARTNER_STATUS_NEW} from '../../services/dict.service';

@Component({
  selector: 'app-dict-partners',
  templateUrl: './dict-partners.component.html',
  styleUrls: ['./dict-partners.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => DictPartnersComponent)}
  ]
})
export class DictPartnersComponent implements OnInit, OnDestroy, ControlValueAccessor {

  @Input() label: string;
  @Input() multiple = false;
  @Input() control: FormControl = null;
  @Input() required = false;
  @Input() filterProfit = false;
  @Input() onlyActive = false;
  @Input() onlyActiveAndNew = false;
  @Input() filterFormOfBusiness = null;
  @Output() selectedPartner: EventEmitter<Partner> = new EventEmitter<Partner>();
  @Output() selectedPartners: EventEmitter<Partner[]> = new EventEmitter<Partner[]>();

  externalControl = true;
  disabled = false;
  items: Partner[] = [];
  itemsSubject: ReplaySubject<ListResponse<Partner>> = new ReplaySubject<ListResponse<Partner>>(1);
  value?: string = null;
  selectedValue?: string = null;
  partnerFilterCtrl: FormControl;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private partnerService: PartnerService,
    @Inject(DOCUMENT) private document: Document,
    private fb: FormBuilder,
  ) {
    this.partnerFilterCtrl = fb.control('');
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  ngOnInit(): void {
    if (this.control === null) {
      this.control = new FormControl('');
      this.externalControl = false;
    }

    this.control.valueChanges
      .pipe(
        startWith(this.control.value),
        takeUntil(this.destroyed$)
      )
      .subscribe((val: string) => {
        if (val) {
          let exists = false;
          this.items.forEach((partner: Partner) => {
            if (partner.uuid === val) {
              exists = true;
            }
          });

          if (!exists && val) {
            this.partnerService
              .loadNotCountedList(
                0,
                9999,
                {in$uuid: val},
                this.itemsSubject
              );
          }
        }
      });

    this.partnerFilterCtrl.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((val) => {
        const partnerActive = this.onlyActive ? PARTNER_STATUS_ACTIVE : null;
        const partnerActiveNew = this.onlyActiveAndNew ? [PARTNER_STATUS_ACTIVE, PARTNER_STATUS_NEW] : null;
        if (val) {
          this.partnerService.loadNotCountedList(0, 3, {
            context: val,
            profit: this.filterProfit ? true : null,
            statusKeys: partnerActive ?? partnerActiveNew,
            formOfBusiness: this.filterFormOfBusiness ?  this.filterFormOfBusiness : null,
          }, this.itemsSubject);
        } else if (this.control.value) {
          this.partnerService
            .loadNotCountedList(
              0,
              9999,
              {in$uuid: this.control.value},
              this.itemsSubject
            );
        }
      });

    this.itemsSubject
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: ListResponse<Partner>) => {
        this.items = result.items;

        if (this.multiple) {
          const selected = [];
          this.items.forEach((partner: Partner) => {
            if (this.control.value?.includes(partner.uuid)) {
              selected.push(partner);
            }

            this.selectedPartners.emit(selected);
          });
        } else {
          this.items.forEach((partner: Partner) => {
            if (partner.uuid === this.control.value) {
              this.selectedPartner.emit(partner);
            }
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (!value) {
      this.selectedValue = '';
    }
    if (this.externalControl) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    this.value = e.value;
    this.onChange(this.value);
  }

  clearValue(): void {
    this.control.setValue(this.multiple ? [] : '');
    this.writeValue(this.multiple ? [] : '');
    this.onChange(this.value);
  }

}
